import {
  Component,
  effect,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { ContentItemComponent } from "./content-item.component";
import { userContextStore } from "../../../../../shared/data/user-context.store";
import { channelLayoutStore } from "../../data/channel-layout.store";
import { ContentSkeletonComponent } from "../../ui/content-skeleton/content-skeleton";

@Component({
  selector: "claas-coremedia-content-renderer",
  templateUrl: "./coremedia-content-renderer.html",
  styleUrls: ["./coremedia-content-renderer.scss"],
  providers: [channelLayoutStore],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ContentItemComponent, ContentSkeletonComponent],
})
export class CoreMediaContentRendererComponent {
  /** The CoreMedia CMChannel ID — resolves Stage 1 (channel layout). */
  readonly contentId = input.required<string>();
  /** Override the site name used for X-Site-Hash (menuLink targets live in a different site). */
  readonly contentSiteName = input<string | undefined>(undefined);
  /**
   * When true, loads the channel resolved to a single locale (X-Language, with
   * X-Fallback-Language fallback). Used for the configurable 404 channel — never emits
   * `notFound` itself to avoid recursively rendering another 404 page.
   */
  readonly localized = input(false);
  /**
   * The CoreMedia root.name of the `contentId` channel's own site, required when `localized()` is
   * true — that channel can live in a different site than the current navigation channel, so its
   * X-Site-Hash must be computed from this value instead of the locale-resolved site.
   */
  readonly siteName = input("");
  /**
   * Emitted whenever the channel could not be loaded or is empty. Never emitted when
   * `localized()` is true. Backend errors (incl. 5xx) are never shown to the user directly —
   * consumers should react by showing the 404 page instead.
   */
  readonly notFound = output<void>();

  protected readonly store = inject(channelLayoutStore);
  private readonly localeStore = inject(userContextStore);

  constructor() {
    effect(() => {
      const id = this.contentId();
      const localized = this.localized();
      const overrideSiteName = this.contentSiteName();
      // Read locale signals to re-trigger this effect on country/language change
      this.localeStore.country();
      this.localeStore.language();
      if (localized) {
        this.store.loadLocalizedChannel({
          channelId: id,
          siteName: this.siteName(),
        });
      } else if (overrideSiteName) {
        this.store.loadChannelWithSiteName({
          channelId: id,
          siteName: overrideSiteName,
        });
      } else {
        this.store.loadChannel(id);
      }
    });

    effect(() => {
      if (!this.localized() && this.store.notFound()) {
        this.notFound.emit();
      }
    });
  }
}
